/** Workspace Builder — create, edit and delete reusable command workspaces. */
import React, { useEffect, useState } from "react";
import { Layers, Plus, Trash2, ChevronUp, ChevronDown, X, Search } from "lucide-react";
import { api, track } from "./commandApi";

const Btn = ({ children, onClick, testid, danger, primary }) => (
  <button type="button" onClick={onClick} data-testid={testid}
          className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1.5 border transition-colors ${
            primary ? "border-[#D92D20] text-[#E4DFD3] bg-[#1a0808] hover:bg-[#D92D20]/20"
              : danger ? "border-[#2D2A26] text-[#D92D20]/80 hover:border-[#D92D20]"
                : "border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]"}`}>
    {children}
  </button>
);

export default function WorkspaceBuilder({ workspaces, onDone }) {
  const [editing, setEditing] = useState(null);   // {name, items, original}
  const [name, setName] = useState("");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setHits([]); return undefined; }
    const t = setTimeout(() => {
      api(`/command/search?q=${encodeURIComponent(q)}`)
        .then((d) => setHits((d.results || []).filter((r) => r.url).slice(0, 6)))
        .catch(() => setHits([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  const startEdit = (ws) => {
    setEditing({ original: ws?.name || null });
    setName(ws?.name || "");
    setItems(ws?.items ? [...ws.items] : []);
    setQ(""); setHits([]);
  };

  const move = (i, d) => setItems((arr) => {
    const n = [...arr]; const j = i + d;
    if (j < 0 || j >= n.length) return arr;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const save = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    const ws = { name: name.trim(), items };
    if (editing?.original) await track("workspace_update", null, { ...ws, original_name: editing.original });
    else await track("workspace_save", null, ws);
    setSaving(false);
    onDone(true);
  };

  const remove = async (wsName) => {
    await track("workspace_delete", null, { name: wsName });
    setEditing(null);
    onDone(false);
  };

  if (!editing) {
    return (
      <div className="px-4 py-3" data-testid="workspace-builder">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20]">
            // Workspaces
          </span>
          <Btn onClick={() => startEdit(null)} testid="ws-new" primary>
            <Plus size={9} className="inline mr-1 -mt-0.5" />New Workspace
          </Btn>
        </div>
        {(workspaces || []).map((w) => (
          <div key={w.name} className="flex items-center gap-3 py-1.5 border-b border-[#2D2A26]/50"
               data-testid={`ws-row-${w.name}`}>
            <Layers size={12} className="text-[#5A554C]" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[12px] text-[#E4DFD3]">{w.name}</div>
              <div className="font-mono text-[9px] text-[#5A554C]">{(w.items || []).length} operations</div>
            </div>
            <Btn onClick={() => startEdit(w)} testid={`ws-edit-${w.name}`}>Edit</Btn>
            <Btn onClick={() => remove(w.name)} testid={`ws-delete-${w.name}`} danger>
              <Trash2 size={10} />
            </Btn>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="px-4 py-3" data-testid="workspace-editor">
      <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20] mb-2">
        // {editing.original ? "Edit Workspace" : "New Workspace"}
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)}
             placeholder="workspace name…" data-testid="ws-name-input"
             className="w-full bg-transparent border border-[#2D2A26] px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3] outline-none focus:border-[#D92D20]/60 placeholder:text-[#454038]" />

      <div className="mt-2">
        {items.map((it, i) => (
          <div key={`${it.url}${i}`} className="flex items-center gap-2 py-1 border-b border-[#2D2A26]/40"
               data-testid={`ws-item-${i}`}>
            <span className="flex-1 font-mono text-[11px] text-[#C9C2B4] truncate">{it.title}</span>
            <button onClick={() => move(i, -1)} data-testid={`ws-item-up-${i}`} className="text-[#5A554C] hover:text-[#E4DFD3]"><ChevronUp size={12} /></button>
            <button onClick={() => move(i, 1)} data-testid={`ws-item-down-${i}`} className="text-[#5A554C] hover:text-[#E4DFD3]"><ChevronDown size={12} /></button>
            <button onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))}
                    data-testid={`ws-item-remove-${i}`} className="text-[#5A554C] hover:text-[#D92D20]"><X size={12} /></button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="font-mono text-[9px] text-[#5A554C] py-2">// add operations below</div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2 border border-[#2D2A26] px-2 py-1.5">
        <Search size={11} className="text-[#5A554C]" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="search records & tools to add…" data-testid="ws-add-search"
               className="flex-1 bg-transparent outline-none font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#454038]" />
      </div>
      {hits.map((h) => (
        <button key={h.id} type="button"
                onClick={() => { setItems((arr) => [...arr, { title: h.title, url: h.url, icon: h.icon }].slice(0, 12)); setQ(""); }}
                data-testid={`ws-add-${h.id}`}
                className="w-full text-left px-2 py-1 font-mono text-[11px] text-[#A39E93] hover:bg-[#181008] hover:text-[#E4DFD3]">
          + {h.title} <span className="text-[#5A554C] text-[9px]">{h.group}</span>
        </button>
      ))}

      <div className="mt-3 flex gap-2">
        <Btn onClick={save} testid="ws-save" primary>{saving ? "Saving…" : "Save Workspace"}</Btn>
        <Btn onClick={() => setEditing(null)} testid="ws-cancel">Back</Btn>
        {editing.original && (
          <Btn onClick={() => remove(editing.original)} testid="ws-delete-editing" danger>Delete</Btn>
        )}
      </div>
    </div>
  );
}
